import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("session_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors globally (session expired)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect for auth endpoints (they handle their own flow)
    const isAuthEndpoint = error.config?.url?.includes("/auth/");
    if (error.response?.status === 401 && !isAuthEndpoint) {
      localStorage.removeItem("session_token");
      // Only redirect if we're not already on login page
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post("/auth/register", data),
  login: (data) => api.post("/auth/login", data),
  me: () => api.get("/auth/me"),
  refresh: () => api.get("/auth/refresh"),
  logout: () => api.post("/auth/logout"),
};

// ATS
export const atsAPI = {
  analyze: (data) => api.post("/ats/analyze", data),
};

// NHS SI Generator
export const nhsAPI = {
  generate: (data) => api.post("/nhs/generate", data),
  status: (requestId) => api.get(`/nhs/status/${requestId}`),
  reanalyze: (data) => api.post("/nhs/reanalyze", data),
};

// Band Coaching
export const coachingAPI = {
  generate: (data) => api.post("/coaching/generate", data),
};

// CV Rewriter
export const cvAPI = {
  rewrite: (data) => api.post("/cv/rewrite", data),
};

// User
export const userAPI = {
  getUsage: () => api.get("/user/usage"),
  usage: () => api.get("/user/usage"),
  history: () => api.get("/user/history"),
  init: () => api.get("/user/init"),
  profile: () => api.get("/user/profile"),
  updateProfile: (data) => api.put("/user/profile", data),
  changePassword: (data) => api.put("/user/change-password", data),
  deleteAccount: () => api.delete("/user/account"),
};

// Payments
export const paymentsAPI = {
  checkout: (data) => api.post("/payments/checkout", data),
  status: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Interview
export const interviewAPI = {
  generate: (data) => api.post("/interview/generate", data),
  evaluate: (data) => api.post("/interview/evaluate", data),
};

// Export
export const exportAPI = {
  docx: (data) => api.post("/export/docx", data, { responseType: "blob" }),
};

// Admin
export const adminAPI = {
  stats: () => api.get("/admin/stats"),
  users: (params) => api.get("/admin/users", { params }),
  updateUser: (userId, data) => api.put(`/admin/users/${userId}`, data),
  stories: () => api.get("/admin/stories"),
  approveStory: (storyId) => api.put(`/admin/stories/${storyId}/approve`),
  feedback: () => api.get("/admin/feedback"),
  costStats: () => api.get("/admin/cost-stats"),
};

export const sessionAPI = {
  reset: (payload) => api.post("/session/reset", payload),
};

// Upload
export const uploadAPI = {
  pdf: (file) => {
    const formData = new FormData();
    formData.append("file", file);
    return api.post("/upload/pdf", formData, { headers: { "Content-Type": "multipart/form-data" } });
  },
};

// NHS Context
export const nhsContextAPI = {
  query: (queryText, band) => api.get("/nhs/context", { params: { query: queryText, band } }),
};

// Success Stories
export const storiesAPI = {
  submit: (data) => api.post("/success-stories", data),
  list: () => api.get("/success-stories"),
};

// Feedback
export const feedbackAPI = {
  submit: (data) => api.post("/feedback", data),
};

export default api;
